import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  SelectChangeEvent,
  Stack,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as FileDownloadIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  FlashOn as FlashOnIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy } from 'firebase-shim';
// import { db } from '../firebase/config';
import { Client, PAYMENT_TERMS } from '../types/client';
import { formatDate, formatCurrency } from '../utils/formatters';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AnimatedPage from '../components/AnimatedPage';
import { useTenantMigration } from '../hooks/useTenantMigration';
import * as indexedDBService from '../utils/indexedDBService';
import { getTenantDataService } from '../services/tenant-data-service';
import InvoiceService from '../services/invoiceService';
import { EmailService } from '../utils/emailService';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';

// Define transaction interface
interface Transaction {
  id: string;
  date: string;
  type: 'invoice' | 'payment';
  amount: number;
  reference: string;
  status: string;
  balance: number;
}

interface InventoryItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  quantity?: number;
  hsnCode?: string;
  gstRate?: number;
  stockQuantity?: number;
  sellingPrice?: number;
}

const Clients: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const db = React.useMemo(() => getTenantDataService(currentUser, userProfile), [currentUser, userProfile]);
  const migration = useTenantMigration(currentUser?.uid, db);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [newClient, setNewClient] = useState<Partial<Client>>({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    gstin: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    paymentTerms: '30 Days',
  });
  const [clientReceivables, setClientReceivables] = useState<{ [key: string]: number }>({});
  const [totalReceivables, setTotalReceivables] = useState(0);
  const [debugData, setDebugData] = useState<any>(null);

  // Initialize refs
  const deleteClientRef = useRef<any>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Ledger related states
  const [openLedgerDialog, setOpenLedgerDialog] = useState(false);
  const [ledgerClient, setLedgerClient] = useState<Client | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>([]);
  const [transactionStatusFilter, setTransactionStatusFilter] = useState<string[]>([]);

  useEffect(() => {
    if (currentUser) {
      fetchClients();
      fetchClientReceivables();
    }
  }, [currentUser]);

  // Add a refresh function
  const refreshClientData = async () => {
    if (currentUser) {
      await fetchClients();
      await fetchClientReceivables();
      await fetchInventory();
    }
  };

  const fetchInventory = async () => {
    try {
      const inventoryData = await db.getAll<any>('inventory');
      setInventoryItems(inventoryData);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  useEffect(() => {
    if (currentUser && migration.done) {
      refreshClientData();
    }
  }, [currentUser, migration.done]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      // Use IndexedDB instead of Firestore
      // const q = query(collection(db, 'clients'), where('userId', '==', currentUser?.uid));
      // const querySnapshot = await getDocs(q);

      const fetchedClients = await db.getAll<Client>('clients');

      setClients(fetchedClients);

      // Save clients to IndexedDB is no longer needed as we fetch FROM it, 
      // but ensure data structure is consistent if needed.
    } catch (error) {
      console.error('Error fetching clients:', error);
      setSnackbar({
        open: true,
        message: 'Error loading clients',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClientReceivables = async () => {
    try {
      // Get all clients to help map missing clientIds
      const clientsData = await indexedDBService.getAllItems<Client>('clients');
      const clientMap = new Map();
      clientsData.forEach(c => {
        clientMap.set(c.id, c);
        if (c.name) clientMap.set(c.name.toLowerCase(), c);
      });

      // First, get all invoices from this user via IndexedDB directly (consistent with Invoices.tsx)
      const invoices = await indexedDBService.getAllItems<any>('invoices');

      const receivables: { [clientId: string]: number } = {};
      let total = 0;

      invoices.forEach(invoice => {
        // Find matching client
        let clientId = invoice.clientId;
        if (!clientId || !clientMap.has(clientId)) {
           let rawClient = invoice.client || invoice.clientName || '';
           let clientName = '';
           if (typeof rawClient === 'string') {
               clientName = rawClient;
           } else if (rawClient && typeof rawClient === 'object' && rawClient.name) {
               clientName = rawClient.name;
           } else {
               clientName = String(rawClient);
           }
           const matched = clientMap.get(clientName.toLowerCase());
           if (matched) clientId = matched.id;
        }

        // Skip if no client ID found
        if (!clientId) return;

        // Convert status to lowercase for case-insensitive comparison
        const status = (invoice.status || '').toLowerCase();

        // Only include invoices with pending/overdue status
        if (status === 'pending' || status === 'overdue' || status === 'partially_paid') {
          // Robust amount parsing
          let amountVal = invoice.amount;
          if (amountVal === undefined || amountVal === null) amountVal = invoice.totalAmount;
          if (typeof amountVal === 'string') amountVal = amountVal.replace(/[^0-9.-]+/g, '');
          const amount = Number(amountVal || 0);

          if (amount > 0) {
            // Add to client's total receivables
            receivables[clientId] = (receivables[clientId] || 0) + amount;
            total += amount;
          }
        }
      });

      setClientReceivables(receivables);
      setTotalReceivables(total);
      setDebugData(invoices.map((i: any) => ({
        id: i.id, client: i.client, clientId: i.clientId, status: i.status, 
        amount: i.amount, totalAmount: i.totalAmount 
      })));
    } catch (error) {
      console.error('Error fetching client receivables:', error);
    }
  };

  const handleOpenDialog = (client?: Client) => {
    if (client) {
      setSelectedClient(client);
      setNewClient(client);
    } else {
      setSelectedClient(null);
      setNewClient({
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        gstin: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        paymentTerms: '30 Days',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedClient(null);
    setNewClient({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      gstin: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      paymentTerms: '30 Days',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewClient({
      ...newClient,
      [name]: value
    });
  };

  const handleSaveClient = async () => {
    if (!currentUser) return;

    // Validate required fields
    if (!newClient.name) {
      setSnackbar({
        open: true,
        message: 'Client name is required',
        severity: 'error'
      });
      return;
    }

    try {
      const clientData = {
        ...newClient,
        userId: currentUser.uid,
        updatedAt: new Date().toISOString(),
        createdAt: selectedClient ? selectedClient.createdAt : new Date().toISOString(),
      };

      let clientId = selectedClient?.id;

      if (!clientId) {
        // Generate a new ID if creating
        // We need to import uuid or just use a random string. 
        // indexedDBService usually handles IDs if we want, but explicit is better for linking.
        // Let's use crypto.randomUUID() or similar if available, or just rely on putItem generating one if we don't pass it?
        // Actually indexedDBService.putItem takes items with optional ID. But best to generate one.
        // Assuming putItem handles add/update effectively.
        // Let's generate a simple ID if missing.
        clientId = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      }

      await db.put('clients', {
        ...clientData,
        id: clientId
      });

      setSnackbar({
        open: true,
        message: `Client ${selectedClient ? 'updated' : 'added'} successfully`,
        severity: 'success',
      });
      handleCloseDialog();
      await refreshClientData();
    } catch (error) {
      console.error('Error saving client:', error);
      setSnackbar({
        open: true,
        message: 'Error saving client',
        severity: 'error'
      });
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await db.remove('clients', clientId);

        await refreshClientData();
        setSnackbar({
          open: true,
          message: 'Client deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting client:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting client',
          severity: 'error'
        });
      }
    }
  };

  const handleOneClickInvoice = async (client: Client) => {
    if (!currentUser) return;
    
    if (!client.defaultItems || client.defaultItems.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please set Default Products/Services for this client first.',
        severity: 'error'
      });
      handleOpenDialog(client); // Open dialog to let them set it
      return;
    }

    setLoading(true);
    try {
      // Pre-check inventory
      const inventoryData = await db.getAll('inventory');
      const missingStockItems = [];
      
      for (const item of client.defaultItems) {
        const qty = item.quantity || 1;
        const invId = item.inventoryItemId || item.id;
        if (invId) {
          const invItem = inventoryData.find((i: any) => i.id === invId);
          if (invItem) {
             const currentStock = invItem.stockQuantity !== undefined ? invItem.stockQuantity : invItem.stock;
             if (currentStock !== undefined && Number(currentStock) < qty) {
               missingStockItems.push(`${item.name} (Need: ${qty}, Have: ${currentStock})`);
             }
          }
        }
      }

      if (missingStockItems.length > 0) {
        setSnackbar({
          open: true,
          message: `Cannot generate invoice. Insufficient stock for: ${missingStockItems.join(', ')}`,
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      // 1. Generate invoice number
      const invoiceNumber = await InvoiceService.generateInvoiceNumber(
        currentUser.uid,
        'INV' // Or get business profile prefix
      );

      // 2. Construct invoice data
      const taxRate = 18; // Default GST 
      let totalSubtotal = 0;
      let totalTaxAmount = 0;

      const items = client.defaultItems.map((item, index) => {
        const qty = item.quantity || 1;
        const price = item.price || 0;
        const amount = qty * price;
        const taxAmount = (amount * taxRate) / 100;
        
        totalSubtotal += amount;
        totalTaxAmount += taxAmount;

        return {
          id: Date.now() + index,
          name: item.name,
          description: 'Auto-generated via 1-Click',
          hsnCode: '',
          quantity: qty,
          rate: price,
          gstRate: taxRate,
          discount: 0,
          amount: amount,
          taxAmount: taxAmount,
          total: amount + taxAmount,
          inventoryItemId: item.inventoryItemId || item.id,
        };
      });

      const grandTotal = totalSubtotal + totalTaxAmount;

      const invoiceData = {
        userId: currentUser.uid,
        invoiceNumber,
        date: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        client: client.name,
        clientId: client.id,
        clientName: client.name,
        clientDetails: {
            address: client.address || '',
            gstin: client.gstin || '',
            email: client.email || '',
            phone: client.phone || '',
            state: client.state || '',
        },
        items: items,
        subtotal: totalSubtotal,
        tax: totalTaxAmount,
        discount: 0,
        amount: grandTotal,
        taxType: 'gst',
        taxRate: taxRate,
        discountType: 'fixed',
        discountValue: 0,
        taxable: true,
        status: 'pending' as 'pending',
        notes: 'Thank you for your business.',
        termsAndConditions: 'Payment is due within 15 days.',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        bankDetails: {},
      };

      // 3. Fetch Business Profile to attach Bank Details & QR code
      let userBusinessName = 'Us';
      let smtpSettings = undefined;
      let userBusinessProfile: any = {};
      try {
        const businesses = await indexedDBService.getAllItems<any>('businesses');
        const userBusiness = businesses.find(b => b.userId === currentUser.uid);
        if (userBusiness) {
          userBusinessProfile = userBusiness;
          userBusinessName = userBusiness.name || 'Us';
          if (userBusiness.bankDetails) {
            invoiceData.bankDetails = userBusiness.bankDetails;
          }
        }
        
        const settings = await indexedDBService.getItemById<any>('userSettings', currentUser.uid);
        if (settings) {
          if (settings.smtpSettings) {
            smtpSettings = settings.smtpSettings;
          }
          if (settings.businessProfile) {
            userBusinessProfile = {
              ...userBusinessProfile,
              ...settings.businessProfile
            };
            if (settings.businessProfile.bankDetails) {
              invoiceData.bankDetails = settings.businessProfile.bankDetails;
            }
          }
        }
      } catch (e) {
        console.error('Failed to load business details', e);
      }

      await InvoiceService.createInvoice(invoiceData, currentUser.uid);

      let successMessage = `⚡ 1-Click Invoice #${invoiceNumber} created for ${client.name}!`;

      // 4. Auto-send email if enabled
      if (client.autoSendEmail && client.email) {
        try {
          const subject = `Invoice #${invoiceNumber} from ${userBusinessName}`;
          const body = `Dear ${client.name},\n\nPlease find the details for Invoice #${invoiceNumber} for the amount of ₹${grandTotal.toFixed(2)}.\n\nThank you for your business.`;
          const pdfBytes = await generateInvoicePDF(invoiceData, userBusinessProfile, 1);
          await EmailService.sendInvoiceEmail(client.email, subject, body, pdfBytes, `Invoice_${invoiceNumber}.pdf`, smtpSettings);
          successMessage = `⚡ 1-Click Invoice #${invoiceNumber} created and emailed to ${client.name}!`;
        } catch (emailError) {
          console.error('Failed to auto-send email', emailError);
          // Keep the message as just created, but maybe log it
        }
      }

      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success'
      });
      await fetchClientReceivables(); // Update UI
    } catch (error) {
      console.error('Error generating 1-click invoice:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate invoice',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Sort function
  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedClients = () => {
    // First apply search filter
    const searchFilteredClients = clients.filter(client => {
      if (!searchQuery) return true;

      const query = searchQuery.toLowerCase();
      return (
        client.name.toLowerCase().includes(query) ||
        client.contactPerson.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query) ||
        client.phone.includes(query) ||
        client.gstin.toLowerCase().includes(query)
      );
    });

    // Then apply sorting
    const sortableClients = [...searchFilteredClients];
    if (sortConfig.key) {
      sortableClients.sort((a, b) => {
        // Special case for pending amount
        if (sortConfig.key === 'pendingAmount') {
          const aAmount = clientReceivables[a.id] || 0;
          const bAmount = clientReceivables[b.id] || 0;
          return sortConfig.direction === 'asc' ? aAmount - bAmount : bAmount - aAmount;
        }

        // Handle possible undefined values
        const valueA = a[sortConfig.key as keyof Client];
        const valueB = b[sortConfig.key as keyof Client];

        // Check if either value is undefined
        if (valueA === undefined && valueB === undefined) return 0;
        if (valueA === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valueB === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

        // Normal string/number comparison for other fields
        if (valueA < valueB) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableClients;
  };

  const toggleClientExpansion = (clientId: string) => {
    setExpandedClient(expandedClient === clientId ? null : clientId);
  };

  // Get formatted amount
  const formatAmount = (amount: number | null | undefined) => {
    // Make sure we have a valid number to format
    const numberAmount = Number(amount || 0);

    // Format using Intl.NumberFormat for proper currency display
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    }).format(numberAmount);
  };

  // Format date for consistent display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Add CSV export function
  const exportToCSV = () => {
    if (clients.length === 0) return;

    const headers = ['Name', 'GSTIN', 'Email', 'Phone', 'Contact Person', 'City', 'State', 'Pending Amount'];

    const csvRows = [
      headers.join(','),
      ...getSortedClients().map(client => {
        const pendingAmount = clientReceivables[client.id] || 0;
        return [
          client.name,
          client.gstin || 'N/A',
          client.email || 'N/A',
          client.phone || 'N/A',
          client.contactPerson || 'N/A',
          client.city || 'N/A',
          client.state || 'N/A',
          pendingAmount.toFixed(2)
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `clients_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // New functions for ledger functionality
  const handleOpenLedgerDialog = async (client: Client) => {
    setLedgerClient(client);
    setOpenLedgerDialog(true);
    await fetchClientTransactions(client.id);
  };

  const handleCloseLedgerDialog = () => {
    setOpenLedgerDialog(false);
    setLedgerClient(null);
    setTransactions([]);
  };

  const fetchClientTransactions = async (clientId: string) => {
    if (!currentUser) return;

    setLoadingTransactions(true);
    setLoadingTransactions(true);
    try {
      const allTransactions: Transaction[] = [];
      let runningBalance = 0;

      // Fetch invoices
      const invoices = await db.getAll<any>('invoices');
      const clientInvoices = invoices.filter((inv: any) => inv.clientId === clientId);

      // Convert invoices to transactions
      clientInvoices.forEach(invoice => {
        const invoiceAmount = Number(invoice.amount || invoice.totalAmount || 0);

        // Don't calculate running balance yet, we need to sort first

        allTransactions.push({
          id: invoice.id,
          date: invoice.date,
          type: 'invoice',
          amount: invoiceAmount,
          reference: invoice.invoiceNumber || invoice.id,
          status: invoice.status || 'unknown',
          balance: 0 // Will calculate later
        });
      });

      // Fetch payments
      const payments = await db.getAll<any>('payments');
      const clientPayments = payments.filter((p: any) => p.clientId === clientId);

      // Convert payments to transactions
      clientPayments.forEach(payment => {
        const paymentAmount = Number(payment.amount || 0);

        allTransactions.push({
          id: payment.id,
          date: payment.date,
          type: 'payment',
          amount: -paymentAmount, // Negative amount for payments
          reference: payment.reference || payment.id,
          status: payment.status || 'completed',
          balance: 0 // Will calculate later
        });
      });

      // Sort all transactions by date
      allTransactions.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateA - dateB;
      });

      // Recalculate running balance to account for merged sort
      runningBalance = 0;
      for (let i = 0; i < allTransactions.length; i++) {
        runningBalance += allTransactions[i].amount;
        allTransactions[i].balance = runningBalance;
      }

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching client transactions:', error);
      setSnackbar({
        open: true,
        message: 'Error loading transaction ledger',
        severity: 'error'
      });
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleTransactionTypeFilterChange = (event: SelectChangeEvent<typeof transactionTypeFilter>) => {
    const value = event.target.value;
    setTransactionTypeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleTransactionStatusFilterChange = (event: SelectChangeEvent<typeof transactionStatusFilter>) => {
    const value = event.target.value;
    setTransactionStatusFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const filteredTransactions = transactions.filter(transaction => {
    // Filter by date range
    if (startDate && new Date(transaction.date) < startDate) return false;
    if (endDate && new Date(transaction.date) > endDate) return false;

    // Filter by transaction type
    if (transactionTypeFilter.length > 0 && !transactionTypeFilter.includes(transaction.type)) return false;

    // Filter by status
    if (transactionStatusFilter.length > 0 && !transactionStatusFilter.includes(transaction.status)) return false;

    return true;
  });

  const exportLedgerToCSV = () => {
    if (!ledgerClient || filteredTransactions.length === 0) return;

    const headers = ['Date', 'Type', 'Reference', 'Amount', 'Status', 'Running Balance'];

    const csvRows = [
      headers.join(','),
      ...filteredTransactions.map(transaction => {
        // Format the date using our helper function
        const date = formatDate(transaction.date);
        // Format the amount with proper sign
        const amount = transaction.amount.toFixed(2);
        // Format the balance
        const balance = transaction.balance.toFixed(2);

        return [
          date,
          transaction.type,
          transaction.reference,
          amount,
          transaction.status,
          balance
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${ledgerClient.name}_ledger_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Client Management
          </Typography>

          <Box display="flex" alignItems="center">
            <TextField
              placeholder="Search clients..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{ mr: 2, width: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)',
                color: 'white',
                fontWeight: 'medium',
              }}
            >
              Add Client
            </Button>
          </Box>
        </Box>

        {/* Summary */}

        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 87, 34, 0.05) 100%)',
                border: '1px solid rgba(255, 152, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                    color: '#FF9800',
                    mr: 2
                  }}
                >
                  <BusinessIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Clients
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF9800' }}>
                {clients.length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.15) 0%, rgba(244, 67, 54, 0.05) 100%)',
                border: '1px solid rgba(255, 87, 34, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(255, 87, 34, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 87, 34, 0.2)',
                    color: '#FF5722',
                    mr: 2
                  }}
                >
                  <AccountBalanceIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Receivables
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF5722' }}>
                {formatCurrency(totalReceivables)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(233, 30, 99, 0.05) 100%)',
                border: '1px solid rgba(244, 67, 54, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(244, 67, 54, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    color: '#F44336',
                    mr: 2
                  }}
                >
                  <ReceiptIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Active Clients
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#F44336' }}>
                {clients.filter(client => client.isActive !== false).length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>



        {/* Actions Row */}
        <Box mb={3} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshClientData}
            sx={{ mr: 2 }}
          >
            Refresh Data
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
          >
            Export to CSV
          </Button>
        </Box>

        {/* Clients Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
            overflowX: 'auto',
            width: '100%'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)' }}>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'name'}
                      direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('name')}
                      sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                    >
                      Client Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Contact Person</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>GSTIN</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'city'}
                      direction={sortConfig.key === 'city' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('city')}
                      sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                    >
                      City
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Outstanding</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedClients().map((client) => (
                  <React.Fragment key={client.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar
                            sx={{
                              bgcolor: 'primary.light',
                              width: 32,
                              height: 32,
                              mr: 1,
                              fontSize: '0.875rem'
                            }}
                          >
                            {client.name.charAt(0)}
                          </Avatar>
                          {client.name}
                        </Box>
                      </TableCell>
                      <TableCell>{client.contactPerson || '-'}</TableCell>
                      <TableCell>{client.phone || '-'}</TableCell>
                      <TableCell>{client.email || '-'}</TableCell>
                      <TableCell>{client.gstin || '-'}</TableCell>
                      <TableCell>{client.city || '-'}</TableCell>
                      <TableCell>
                        {clientReceivables[client.id] ? (
                          <Typography fontWeight="bold" color="error.main">
                            {formatCurrency(clientReceivables[client.id])}
                          </Typography>
                        ) : (
                          <Typography color="success.main">
                            No outstanding
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="⚡ 1-Click Invoice">
                            <IconButton size="small" onClick={() => handleOneClickInvoice(client)} color="warning">
                              <FlashOnIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Transaction History">
                            <IconButton size="small" onClick={() => handleOpenLedgerDialog(client)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Client">
                            <IconButton size="small" onClick={() => handleOpenDialog(client)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Client">
                            <IconButton size="small" onClick={() => handleDeleteClient(client.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {getSortedClients().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        No clients found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Client Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {selectedClient ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} p={1}>
              <TextField
                label="Name"
                name="name"
                value={newClient.name}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Contact Person"
                name="contactPerson"
                value={newClient.contactPerson}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={newClient.email}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Phone"
                name="phone"
                value={newClient.phone}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="GSTIN"
                name="gstin"
                value={newClient.gstin}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Address"
                name="address"
                value={newClient.address}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="City"
                name="city"
                value={newClient.city}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="State"
                name="state"
                value={newClient.state}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Pincode"
                name="pincode"
                value={newClient.pincode}
                onChange={handleInputChange}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Payment Terms</InputLabel>
                <Select
                  value={newClient.paymentTerms}
                  label="Payment Terms"
                  onChange={(e) => setNewClient({ ...newClient, paymentTerms: e.target.value })}
                >
                  {PAYMENT_TERMS.map((term) => (
                    <MenuItem key={term} value={term}>
                      {term}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle2" color="primary">Invoicing Automation</Typography>
                <Typography variant="caption" color="textSecondary">Used for 1-Click and Zero-Click invoices</Typography>
              </Box>

              <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                {(newClient.defaultItems || []).map((item: any, index: number) => (
                  <Grid container spacing={2} key={item.id || index} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid item xs={12} sm={5}>
                      <Autocomplete
                        freeSolo
                        options={inventoryItems}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        isOptionEqualToValue={(option, value) => {
                          const optionName = typeof option === 'string' ? option : option.name;
                          const valueName = typeof value === 'string' ? value : value.name;
                          return optionName === valueName;
                        }}
                        value={inventoryItems.find(inv => inv.id === item.inventoryItemId) || item.name}
                        onChange={(e, newValue) => {
                          const items = [...(newClient.defaultItems || [])];
                          
                          let selectedItem = newValue;
                          if (typeof newValue === 'string') {
                            const match = inventoryItems.find(inv => inv.name.toLowerCase() === newValue.toLowerCase());
                            if (match) selectedItem = match;
                          }

                          if (typeof selectedItem === 'string') {
                            items[index] = { ...items[index], name: selectedItem, inventoryItemId: undefined };
                          } else if (selectedItem) {
                            let qty = items[index].quantity || 1;
                            const stock = selectedItem.stockQuantity || 0;
                            if (qty > stock) {
                              qty = stock;
                              setSnackbar({
                                open: true,
                                message: `Stock alert: Only ${stock} units available.`,
                                severity: 'error'
                              });
                            }
                            items[index] = { 
                              ...items[index], 
                              name: selectedItem.name, 
                              price: selectedItem.sellingPrice || selectedItem.price || 0, 
                              inventoryItemId: selectedItem.id,
                              quantity: qty
                            };
                          } else {
                            items[index] = { ...items[index], name: '', inventoryItemId: undefined };
                          }
                          setNewClient({ ...newClient, defaultItems: items });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Product/Service Name"
                            placeholder="e.g. Monthly Retainer"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Qty"
                        InputLabelProps={{ shrink: true }}
                        value={item.quantity === undefined ? '' : item.quantity}
                        onChange={(e) => {
                          const items = [...(newClient.defaultItems || [])];
                          const rawValue = e.target.value;
                          let newQty: number | undefined = rawValue === '' ? undefined : Number(rawValue);
                          
                          if (item.inventoryItemId && newQty !== undefined) {
                            const invItem = inventoryItems.find(inv => inv.id === item.inventoryItemId);
                            const stock = invItem?.stockQuantity || 0;
                            if (invItem && newQty > stock) {
                              newQty = stock;
                              setSnackbar({
                                open: true,
                                message: `Stock alert: Only ${stock} units available.`,
                                severity: 'error'
                              });
                            }
                          }
                          
                          items[index] = { ...items[index], quantity: newQty };
                          setNewClient({ ...newClient, defaultItems: items });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Price"
                        disabled={!!item.inventoryItemId}
                        InputLabelProps={{ shrink: true }}
                        value={item.price === 0 || item.price === undefined ? '' : item.price}
                        onChange={(e) => {
                          const items = [...(newClient.defaultItems || [])];
                          items[index] = { ...items[index], price: Number(e.target.value) };
                          setNewClient({ ...newClient, defaultItems: items });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        disabled
                        label="Total"
                        InputLabelProps={{ shrink: true }}
                        value={`₹${((item.quantity || 0) * (item.price || 0)).toFixed(2)}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="error" onClick={() => {
                        const items = [...(newClient.defaultItems || [])];
                        items.splice(index, 1);
                        setNewClient({ ...newClient, defaultItems: items });
                      }}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={() => {
                    const items = [...(newClient.defaultItems || [])];
                    items.push({ id: Date.now().toString(), name: '', quantity: 1, price: 0 });
                    setNewClient({ ...newClient, defaultItems: items });
                  }}
                  variant="outlined" 
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Default Item
                </Button>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={newClient.isZeroClickEnabled || false}
                    onChange={(e) => setNewClient({ ...newClient, isZeroClickEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Enable Zero-Click Auto-Invoicing"
              />

              {newClient.isZeroClickEnabled && (
                <FormControl fullWidth>
                  <InputLabel>Zero-Click Frequency</InputLabel>
                  <Select
                    value={newClient.zeroClickFrequency || 'Monthly'}
                    label="Zero-Click Frequency"
                    onChange={(e) => setNewClient({ ...newClient, zeroClickFrequency: e.target.value as 'Weekly' | 'Monthly' | 'Yearly' })}
                  >
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Monthly">Monthly</MenuItem>
                    <MenuItem value="Yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={newClient.autoSendEmail || false}
                    onChange={(e) => setNewClient({ ...newClient, autoSendEmail: e.target.checked })}
                    color="primary"
                  />
                }
                label="Auto-Send Invoice via Email (1-Click & Zero-Click)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveClient}
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transaction Ledger Dialog */}
        <Dialog open={openLedgerDialog} onClose={handleCloseLedgerDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            Transaction Ledger: {ledgerClient?.name}
          </DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Filter Transactions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Transaction Type</InputLabel>
                      <Select
                        multiple
                        value={transactionTypeFilter}
                        onChange={handleTransactionTypeFilterChange}
                        label="Transaction Type"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={value === 'invoice' ? 'Invoice' : 'Payment'}
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      >
                        <MenuItem value="invoice">Invoice</MenuItem>
                        <MenuItem value="payment">Payment</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        multiple
                        value={transactionStatusFilter}
                        onChange={handleTransactionStatusFilterChange}
                        label="Status"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="overdue">Overdue</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </LocalizationProvider>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Transactions History
              </Typography>
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={exportLedgerToCSV}
                variant="outlined"
                disabled={filteredTransactions.length === 0}
              >
                Export Ledger
              </Button>
            </Box>

            {loadingTransactions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredTransactions.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No transactions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no transactions matching your filters or this client has no transaction history.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Running Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.type === 'invoice' ? 'Invoice' : 'Payment'}
                            color={transaction.type === 'invoice' ? 'info' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{transaction.reference}</TableCell>
                        <TableCell align="right" sx={{
                          color: transaction.amount < 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}>
                          {formatAmount(transaction.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status}
                            color={
                              transaction.status === 'paid' || transaction.status === 'completed' ? 'success' :
                                transaction.status === 'pending' ? 'warning' :
                                  transaction.status === 'overdue' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatAmount(transaction.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLedgerDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AnimatedPage>
  );
};

export default Clients; 
